import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, TitleStrategy } from '@angular/router';
import { ScreenTrackingService } from '@angular/fire/analytics';

import { GroupTabReuseStrategy } from './shared/util/group-tab-reuse.strategy';

import { NgbDateAdapter, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import * as Sentry from '@sentry/angular';
import { ToastrModule } from 'ngx-toastr';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DevErrorHandlerService } from './shared/services/dev-error-handler.service';
import { environment } from '../environments/environment';
import { PrivateLayoutComponent } from './core/layouts/private.layout';
import { PublicLayoutComponent } from './core/layouts/public.layout';
import { SharedModule } from './shared/shared.module';
import { HeaderComponent } from './core/header/header.component';
import { FooterComponent } from './core/footer/footer.component';
import { AdminActionMenuComponent } from './core/admin-action-menu/admin-action-menu.component';
import { CustomDateParserFormatter } from './shared/services/ngb-date-formatter.service';
import { NgbDateYYYYMMDDAdapter } from './shared/ngb-date-YYYYMMDD-adapter.service';
import { ErrorComponent } from './error/error.component';
import { NotFoundComponent } from './error/not-found.component';
import { NotFoundRedirectComponent } from './error/not-found-redirect.component';
import { ContactComponent } from './contact/contact.component';
import { AppTitleStrategy } from './shared/util/app-title.strategy';
import { CoreServicesModule } from './core/core-services.module';
import { UpdateBannerComponent } from './shared/components/update-banner/update-banner.component';

@NgModule({
  declarations: [
    AppComponent,
    PrivateLayoutComponent,
    PublicLayoutComponent,
    HeaderComponent,
    AdminActionMenuComponent,
    FooterComponent,
    ErrorComponent,
    NotFoundComponent,
    NotFoundRedirectComponent,
    ContactComponent,
    UpdateBannerComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule.withConfig({ disableAnimations: prefersReducedMotion() }),
    AppRoutingModule,
    CoreServicesModule,
    SharedModule,
    ToastrModule.forRoot({
      closeButton: true,
      tapToDismiss: false,
      newestOnTop: true,
      positionClass: 'toast-top-right',
      timeOut: 5000,
      extendedTimeOut: 1500,
      titleClass: 'app-toast-title',
      messageClass: 'app-toast-message',
      toastClass: 'ngx-toastr app-toast',
    }),
  ],
  providers: [
    ScreenTrackingService,
    { provide: RouteReuseStrategy, useClass: GroupTabReuseStrategy },
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    { provide: NgbDateAdapter, useClass: NgbDateYYYYMMDDAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          // Keep PrimeNG styles in a CSS layer so the app's Bootstrap styles win.
          cssLayer: { name: 'primeng', order: 'primeng' },
        },
      },
    }),
    environment.enableSentry
      // No showDialog: the feedback widget is configured autoInject: false in
      // main.ts so it only ever opens from the "Report a bug" trigger. Forcing
      // it open here put a name-and-email-required modal in front of users for
      // errors they did not cause and often had not noticed. Errors are still
      // reported; only the prompt is gone.
      ? { provide: ErrorHandler, useValue: Sentry.createErrorHandler() }
      : { provide: ErrorHandler, useClass: DevErrorHandlerService },
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
}
